import React, { useEffect, useRef, useState } from "react";
import { codexApi } from "../lib/api";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
    { id: "support", amount: 10, label: "$10" },
    { id: "patron", amount: 25, label: "$25" },
    { id: "benefactor", amount: 100, label: "$100" },
];

export default function DonationCard() {
    const [selected, setSelected] = useState("support");
    const [customAmount, setCustomAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const [statusBusy, setStatusBusy] = useState(false);
    const polledRef = useRef(false);

    // On return from Stripe, poll the status with the session_id Stripe
    // appended to our success_url. We bail after a few attempts to avoid
    // infinite spinning.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("donation_session_id");
        const cancelled = params.get("donation");
        if (cancelled === "cancelled") {
            toast.message("Donation cancelled.");
            const url = new URL(window.location.href);
            url.searchParams.delete("donation");
            window.history.replaceState({}, "", url.toString());
            return;
        }
        if (!sessionId || polledRef.current) return;
        polledRef.current = true;

        async function poll(attempt = 0) {
            if (attempt >= 6) {
                toast.message(
                    "Still processing — check your email for a Stripe receipt.",
                );
                return;
            }
            try {
                setStatusBusy(true);
                const res = await codexApi.donationStatus(sessionId);
                if (res.payment_status === "paid") {
                    toast.success(
                        `Thank you for your $${res.amount_total.toFixed(2)} gift. The Codex stands because of you.`,
                        { duration: 7000 },
                    );
                    // Clean the URL so a reload doesn't repoll.
                    const url = new URL(window.location.href);
                    url.searchParams.delete("donation_session_id");
                    window.history.replaceState({}, "", url.toString());
                    return;
                }
                if (res.status === "expired") {
                    toast.error("Donation session expired.");
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2200);
            } catch {
                setTimeout(() => poll(attempt + 1), 2200);
            } finally {
                setStatusBusy(false);
            }
        }
        poll();
    }, []);

    async function handleDonate() {
        let packageId = selected === "custom" ? null : selected;
        let customAmountUsd = null;
        if (selected === "custom") {
            const n = parseFloat(customAmount);
            if (!n || n < 1) {
                toast.error("Enter an amount of at least $1.");
                return;
            }
            customAmountUsd = n;
        }
        setBusy(true);
        try {
            const res = await codexApi.donationCheckout({
                packageId,
                customAmountUsd,
                originUrl: window.location.origin,
            });
            window.location.href = res.url;
        } catch (e) {
            const detail =
                e?.response?.data?.detail ||
                e?.message ||
                "Could not open the Stripe checkout.";
            toast.error(detail);
            setBusy(false);
        }
    }

    return (
        <section className="px-4" data-testid="donation-card">
            <div className="codex-card max-w-3xl mx-auto px-6 py-7 sm:px-8 sm:py-8 space-y-5">
                <div className="flex items-center justify-center gap-2 text-[#F9E596]">
                    <Heart size={16} strokeWidth={1.6} className="opacity-90" />
                    <h3 className="font-serif-display text-xl sm:text-2xl tracking-wide text-[#F9E596]">
                        Support the Codex
                    </h3>
                </div>
                <p
                    className="text-[#CBD5E1] text-sm leading-relaxed text-center max-w-xl mx-auto"
                    data-testid="donation-card-copy"
                >
                    The OS, the studies, the voice — all kept open by the people who
                    believe in this work. Every gift goes toward keeping the Codex
                    running and the next layer building.
                </p>
                <div
                    className="flex flex-wrap justify-center gap-2"
                    role="radiogroup"
                    aria-label="Donation amount"
                >
                    {PRESETS.map((p) => {
                        const active = selected === p.id;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                data-testid={`donation-preset-${p.id}`}
                                onClick={() => setSelected(p.id)}
                                className={`px-5 py-2 rounded-full font-mono-os text-[12px] tracking-[0.22em] uppercase transition border ${
                                    active
                                        ? "border-[#F9E596] text-[#FFF6D4]"
                                        : "border-[#D4AF37]/40 text-[#94A3B8] hover:text-[#F3D78E] hover:border-[#D4AF37]/70"
                                }`}
                                style={
                                    active
                                        ? {
                                              background:
                                                  "linear-gradient(180deg, rgba(249,229,150,0.28), rgba(212,175,55,0.16))",
                                              boxShadow:
                                                  "inset 0 1px 0 rgba(255,246,212,0.35), 0 0 18px -4px rgba(212,175,55,0.5)",
                                          }
                                        : undefined
                                }
                            >
                                {p.label}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        role="radio"
                        aria-checked={selected === "custom"}
                        data-testid="donation-preset-custom"
                        onClick={() => setSelected("custom")}
                        className={`px-5 py-2 rounded-full font-mono-os text-[12px] tracking-[0.22em] uppercase transition border ${
                            selected === "custom"
                                ? "border-[#F9E596] text-[#FFF6D4]"
                                : "border-[#D4AF37]/40 text-[#94A3B8] hover:text-[#F3D78E] hover:border-[#D4AF37]/70"
                        }`}
                        style={
                            selected === "custom"
                                ? {
                                      background:
                                          "linear-gradient(180deg, rgba(249,229,150,0.28), rgba(212,175,55,0.16))",
                                      boxShadow:
                                          "inset 0 1px 0 rgba(255,246,212,0.35), 0 0 18px -4px rgba(212,175,55,0.5)",
                                  }
                                : undefined
                        }
                    >
                        Custom
                    </button>
                </div>
                {selected === "custom" && (
                    <div className="flex justify-center">
                        <div className="relative w-full max-w-xs">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F9E596] font-mono-os text-sm">
                                $
                            </span>
                            <input
                                type="number"
                                min="1"
                                max="5000"
                                step="1"
                                inputMode="decimal"
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                placeholder="Enter amount"
                                data-testid="donation-custom-input"
                                className="codex-input pl-8 text-center"
                            />
                        </div>
                    </div>
                )}
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={handleDonate}
                        disabled={busy || statusBusy}
                        data-testid="donation-donate-btn"
                        className="codex-button codex-button-primary !px-7 !py-3 !text-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? (
                            <Loader2 size={14} className="animate-spin" strokeWidth={1.6} />
                        ) : (
                            <Heart size={14} strokeWidth={1.6} />
                        )}
                        {busy ? "Opening Stripe…" : "Give Now"}
                    </button>
                </div>
                <p className="text-[10px] text-[#64748B] text-center tracking-[0.2em] uppercase font-mono-os">
                    Secure checkout by Stripe · One-time gift
                </p>
            </div>
        </section>
    );
}
